// PR-33 — Video maliyet guardrail helper'ı (client-side; upload BAŞLAMADAN).
// Büyük/uzun videoyu Storage'a gitmeden reddeder + best-effort poster üretir.
// Yeni bağımlılık YOK (native <video>/<canvas>). MyClips V3 pipeline'ıyla
// ALAKASIZ — yalnız posts (NewPost) + hikaye (Hikayeler) Firebase Storage yolu.

export const MB = 1024 * 1024;

/**
 * Video süresini (sn) metadata'dan oku. Okunamazsa null (hata) döner —
 * çağıran "süre okunamadı" hatası gösterebilir. Object URL temizlenir.
 */
export function readVideoDurationSec(file: File | Blob): Promise<number | null> {
  return new Promise((resolve) => {
    let settled = false;
    let url = "";
    const done = (v: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(guard);
      try { if (url) URL.revokeObjectURL(url); } catch { /* no-op */ }
      resolve(v);
    };
    const guard = setTimeout(() => done(null), 8000); // takılırsa vazgeç → hata
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      (v as any).playsInline = true;
      v.onloadedmetadata = () => {
        const d = Number(v.duration);
        done(Number.isFinite(d) && d > 0 ? d : null);
      };
      v.onerror = () => done(null);
      url = URL.createObjectURL(file);
      v.src = url;
    } catch {
      done(null);
    }
  });
}

export interface VideoLimit {
  maxBytes: number;
  maxSec: number;
  /** Kullanıcı-görünür bağlam: "Story" / "Gönderi" */
  label: string;
}

export interface ValidationResult {
  ok: boolean;
  message?: string;
}

/**
 * Video dosyasını UPLOAD ÖNCESİ doğrula. Boyut (anında) + süre (metadata).
 * ok=false ise çağıran upload'ı başlatmaz + message'ı gösterir.
 */
export async function validateVideoFile(file: File, limit: VideoLimit): Promise<ValidationResult> {
  if (!file || !file.type?.startsWith("video/")) return { ok: true }; // video değil → bu guard atlanır
  const maxMB = Math.round(limit.maxBytes / MB);
  if (file.size >= limit.maxBytes) {
    return {
      ok: false,
      message: `Bu video çok büyük (en fazla ${maxMB} MB). Daha kısa veya daha düşük boyutlu bir video seç.`,
    };
  }
  const dur = await readVideoDurationSec(file);
  if (dur == null) {
    return { ok: false, message: "Video okunamadı. Farklı bir video dene." };
  }
  if (dur > limit.maxSec + 1) {
    return {
      ok: false,
      message: `${limit.label} videoları en fazla ${limit.maxSec} saniye olabilir. Videoyu kısaltıp tekrar dene.`,
    };
  }
  return { ok: true };
}

/**
 * Video'nun ilk karesine yakın bir andan best-effort poster (JPEG Blob) yakala.
 * Server thumbnail pipeline'ı YOK (posts/hikaye Firebase Storage) → client
 * <video>+<canvas>. Herhangi bir adım hata verirse null döner: upload akışını
 * ASLA bloklamaz. iOS Safari için DOM'a eklenmiş görünmez <video> kullanılır.
 */
export function captureVideoPoster(file: File | Blob): Promise<Blob | null> {
  return new Promise((resolve) => {
    let settled = false;
    const v = document.createElement("video");
    v.style.cssText =
      "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;";
    document.body.appendChild(v);
    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(guard);
      try { URL.revokeObjectURL(v.src); } catch { /* no-op */ }
      try { v.remove(); } catch { /* no-op */ }
      resolve(blob || null);
    };
    const guard = setTimeout(() => finish(null), 8000);
    v.muted = true;
    (v as any).playsInline = true;
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      try {
        const d = v.duration;
        v.currentTime = Number.isFinite(d) && d > 0 ? Math.min(0.1, d / 4) : 0.1;
      } catch { finish(null); }
    };
    v.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth || 0;
        canvas.height = v.videoHeight || 0;
        if (!canvas.width || !canvas.height) { finish(null); return; }
        const ctx = canvas.getContext("2d");
        if (!ctx) { finish(null); return; }
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => finish(blob), "image/jpeg", 0.82);
      } catch { finish(null); }
    };
    v.onerror = () => finish(null);
    try { v.src = URL.createObjectURL(file); } catch { finish(null); }
  });
}
